import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../lib/auth/axiosApi';
import Board from './Board';
import { initialSide, applyMoves, pipCount } from './engine';

// Step through a game's moves from the log. Board shown from your side.

function buildSteps(log, viewSide) {
  let board = [initialSide(), initialSide()];
  const steps = [{ board, text: 'start', dice: null, side: null }];
  log.forEach((e) => {
    if (e.type !== 'move') return;
    const s = e.side;
    const [m, o] = applyMoves(board[s], board[1 - s], e.moves || []);
    board = s === 0 ? [m, o] : [o, m];
    steps.push({ board, text: e.text || "can't move", dice: e.dice, side: s, pid: e.pid });
  });
  return steps.map((st) => ({ ...st, me: st.board[viewSide], opp: st.board[1 - viewSide] }));
}

export default function Replay({ tableId, state, onClose }) {
  const [games, setGames] = useState([]);
  const [pick, setPick] = useState(null);
  const [i, setI] = useState(0);
  const viewSide = state.you.side === null || state.you.side === undefined ? 0 : state.you.side;

  useEffect(() => {
    axiosInstance.get(`backgammon/${tableId}/games/`).then((res) => {
      const list = res.data || [];
      const cur = state.game;
      if (cur && cur.phase !== 'game_over' && cur.log.length) {
        list.push({ game_no: state.game_no, current: true, log: cur.log, summary: {} });
      }
      setGames(list);
      if (list.length) setPick(list.length - 1);
    }).catch(() => setGames([]));
    // Snapshot when opened; polling shouldn't yank the picker around.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId]);

  const steps = useMemo(() => (pick === null || !games[pick] ? [] : buildSteps(games[pick].log, viewSide)), [games, pick, viewSide]);
  useEffect(() => { setI(steps.length ? steps.length - 1 : 0); }, [steps.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') setI((x) => Math.max(0, x - 1));
      if (e.key === 'ArrowRight') setI((x) => Math.min(steps.length - 1, x + 1));
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [steps.length, onClose]);

  const name = (pid) => ((state.participants || []).find((p) => p.pid === pid) || {}).name || '';
  const step = steps[i];

  return (
    <div className="bg-modal" onClick={onClose}>
      <div className="bg-modal-body bg-replay" onClick={(e) => e.stopPropagation()}>
        <div className="bg-replay-head">
          <select value={pick === null ? '' : pick} onChange={(e) => setPick(Number(e.target.value))}>
            {games.map((g, idx) => (
              <option key={g.game_no} value={idx}>
                game {g.game_no}{g.current ? ' (in progress)' : ` — ${name(g.winner)} +${g.points}`}
              </option>
            ))}
          </select>
          <button className="bg-btn bg-btn-quiet" onClick={onClose}>close</button>
        </div>
        {step ? (
          <>
            <Board me={step.me} opp={step.opp} cube={null}
              dice={step.dice ? step.dice.map((v) => ({ value: v, used: false })) : null}
              diceOwner={step.side === viewSide ? 'me' : 'opp'}
              labels={{ top: `${pipCount(step.opp)}`, bottom: `${pipCount(step.me)}` }} />
            <div className="bg-replay-controls">
              <button className="bg-btn" onClick={() => setI(0)} disabled={i === 0}>⏮</button>
              <button className="bg-btn" onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0}>◀</button>
              <span className="bg-replay-text">
                {i}/{steps.length - 1} {step.pid ? `${name(step.pid)} ${step.dice ? step.dice.join('-') : ''}: ` : ''}{step.text}
              </span>
              <button className="bg-btn" onClick={() => setI(Math.min(steps.length - 1, i + 1))} disabled={i === steps.length - 1}>▶</button>
              <button className="bg-btn" onClick={() => setI(steps.length - 1)} disabled={i === steps.length - 1}>⏭</button>
            </div>
          </>
        ) : <div className="bg-hint">no finished games yet</div>}
      </div>
    </div>
  );
}
